import { ArrowDownTrayIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { useEffect, useRef, useState } from 'react'
import { analyzePdf, getAnalyzedFileList, getFileDetails, submitAnalyzedSections } from 'services'
import { downloadS3Document } from 'services/apis/documents'
import { Button, Modal, PlainTable } from 'stories/components'
import { confirm, formatTime } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import EditSectionsDialog, { FileItem, SectionRow } from './EditSectionsDialog'

interface AnalyzedFile {
  name: string
  key: string
  createdAt: string
  type: string
  createdBy: string
  data: Record<string, any>
}

export function AnalyzePdf() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sections, setSections] = useState<SectionRow[]>([])
  const [files, setFiles] = useState<FileItem[]>([])
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const [loading, setLoading] = useState(false)
  const [analyzedFiles, setAnalyzedFiles] = useState<AnalyzedFile[]>([])
  const [fetchingList, setFetchingList] = useState(false)
  const [currentFileKey, setCurrentFileKey] = useState<string>('')

  const loanNumber = setLoanNumber()

  useEffect(() => {
    fetchAnalyzedFiles()
  }, [loanNumber])

  const fetchAnalyzedFiles = async () => {
    setFetchingList(true)
    try {
      const result = await getAnalyzedFileList(loanNumber)
      setAnalyzedFiles(result)
    } catch (err) {
      console.error('Failed to fetch analyzed files:', err)
      setAnalyzedFiles([])
    } finally {
      setFetchingList(false)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isPdf = file.type === 'application/pdf'
    const isXml = file.type === 'application/xml' || file.type === 'text/xml'
    if (!isPdf && !isXml) {
      setError('Please select a valid PDF or XML file.')
      e.target.value = ''
      return
    }

    const confirmed = await confirm(`Are you sure you want to analyze "${file.name}"?`)
    if (!confirmed) {
      e.target.value = ''
      return
    }

    setError(null)
    setLastUpdatedAt(Date.now())
    setModalOpen(true)

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const result = await analyzePdf(formData, loanNumber)
      if (result?.success) {
        await fetchAnalyzedFiles()

        if (result?.fileKey) {
          setCurrentFileKey(result.fileKey)
        }

        if (result?.urls) {
          const xmlFileName = file.name
          const pdfFileName = xmlFileName.replace(/\.xml$/i, '.pdf')

          const newFileItems: FileItem[] = result.urls.map((url: string, index: number) => ({
            id: `${Date.now()}-${index}`,
            name: result.urls.length === 1 ? pdfFileName : `${pdfFileName.replace('.pdf', '')}_${index + 1}.pdf`,
            url: url,
          }))
          setFiles([...files, ...newFileItems])
          setSections([])
        } else {
          const sectionsData: SectionRow[] = result.data.sections.map((s: any) => ({
            title: s.title,
            page_start: s.page_start,
            page_end: s.page_end,
          }))
          setSections(sectionsData)
          setError(null)
        }
      } else {
        setError('No sections returned from analysis.')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to analyze PDF.')
    } finally {
      setLoading(false)
      e.target.value = ''
    }
  }

  const onClose = () => {
    setModalOpen(false)
    setSections([])
    setFiles([])
    setError(null)
  }

  const handleUploadAnother = () => {
    setError(null)
    fileInputRef.current?.click()
  }

  const handleDownload = async (fileKey: string, fileName: string) => {
    try {
      await downloadS3Document(fileKey, fileName)
    } catch (err: any) {
      console.error('Failed to download file:', err)
    }
  }

  const handleSubmit = async () => {
    if (files?.length > 0 && !sections.length) {
      handleUploadAnother()
      return
    }

    if (!sections.length) {
      return
    }

    setLoading(true)
    try {
      const result = await submitAnalyzedSections(currentFileKey, sections)
      if (result?.success) {
        await fetchAnalyzedFiles()
        setModalOpen(false)
        setSections([])
        setCurrentFileKey('')
        setFiles([])
        setError(null)
      } else {
        setError('Failed to submit sections.')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to submit sections.')
    } finally {
      setLoading(false)
    }
  }

  const handleShowDetails = async (file: any) => {
    try {
      const result = await getFileDetails(file.id)
      setCurrentFileKey(result.key)
      setSections(result.data.sections)
      setModalOpen(true)
      setLoading(true)
    } catch (err: any) {
      console.log('Failed to get details: ', err?.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="py-6 px-2">
        <Overview title="Analyze" />
        <LayoutLoading show={fetchingList} />

        <div className="max-w-screen-2xl m-auto">
          <div className="relative bg-white shadow1 rounded mb-4 p-4">
            <div className="w-full flex justify-center">
              <Button
                className="relative inline-flex items-center gap-2 px-8 py-3 rounded-md bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white font-semibold shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                onClick={handleButtonClick}
                disabled={loading}
                type="button"
              >
                <span>{loading ? 'Analyzing…' : 'Upload File'}</span>
              </Button>
            </div>

            {analyzedFiles.length > 0 && (
              <div className="mx-2">
                <h2 className="text-xl font-semibold mb-4">Previously Analyzed Documents</h2>
                <div className="relative">
                  <PlainTable
                    header={['No', 'File Name', 'Type', 'Uploaded Date', 'Created By', 'Actions']}
                    data={analyzedFiles.map((file, index) => [
                      index + 1,
                      file.name,
                      file.type === 'pdf' ? 'PDF' : 'XML',
                      formatTime(file.createdAt),
                      file.createdBy,
                      <span className="flex gap-2">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => handleDownload(file.key, file.name)}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </span>
                        {file.type === 'pdf' && (
                          <span
                            className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                            onClick={() => handleShowDetails(file)}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                        )}
                      </span>,
                    ])}
                  />
                </div>
              </div>
            )}

            <input
              type="file"
              accept="application/pdf,application/xml,text/xml,.pdf,.xml"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {modalOpen && (
              <Modal
                title="Analyze File"
                titleCancel="Close"
                titleOkay={loading ? '' : files.length > 0 ? 'Upload Another File' : 'Submit'}
                loading={loading}
                isOpen={modalOpen}
                lastUpdatedAt={lastUpdatedAt}
                onClose={onClose}
                onOk={handleSubmit}
              >
                <div className="min-w-[650px] max-w-[80vw]">
                  {error ? (
                    <pre className="mt-2 text-xs bg-red-50 text-red-700 p-3 rounded border border-red-200 max-h-60 overflow-auto whitespace-pre-wrap">
                      {error}
                    </pre>
                  ) : (
                    <EditSectionsDialog sections={sections} files={files} onChange={setSections} loading={loading} />
                  )}
                </div>
              </Modal>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
